import React, { useCallback, useState, createContext, useContext } from 'react';
import { Product, CartItem } from '../types';
import { products } from '../data/products';
import { useScreenInit } from '../useScreenInit.js';
interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  shippingCost: number;
  total: number;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const screenInit = useScreenInit() as {
    cartSeed?: boolean;
  };
  const [items, setItems] = useState<CartItem[]>(() => screenInit.cartSeed ? products.slice(0, 2).map(product => ({
    product,
    quantity: 1
  })) : []);
  const addItem = useCallback((product: Product, quantity: number = 1) => {
    setItems(prev => {
      const existingItem = prev.find(item => item.product.id === product.id);
      if (existingItem) {
        return prev.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + quantity
        } : item);
      }
      return [...prev, {
        product,
        quantity
      }];
    });
  }, []);
  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  }, []);
  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(productId);
      return;
    }
    setItems(prev => prev.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  }, [removeItem]);
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shippingCost = items.length === 0 ? 0 : subtotal > 5000 ? 0 : 250;
  const total = subtotal + shippingCost;
  return <CartContext.Provider value={{
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount,
    subtotal,
    shippingCost,
    total
  }} data-id="element-877" data-mp="nxqgb7m6pyod">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}